import clsx from "clsx";
import {
  FC,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  OrderRows,
  OrderRowsFallback,
  useAlignedValues,
} from "../../../trade/components/mobile/OrderRow";
import { STICKY_TOP } from "../../../common/components/StickyBar";
import { FinRangeStatus } from "../../analytics/__generated__/AlgorithmicLeaderboardQuery.graphql";
import { Row } from "../../leaderboard/types";
import { LeaderboardRow } from "./LeaderboardRow";

export const LeaderboardRowsFallback: FC = () => (
  <OrderRowsFallback className="algorithmic-leaderboard__rows algorithmic-leaderboard__list" />
);

// Sizes every row's pair column to the widest pair, so the range pips all
// start at the same x. Each head is its own grid, so CSS can't do it alone.
const usePairWidth = (rows: Row[]) => {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return;
    const pairs = [
      ...list.querySelectorAll<HTMLElement>(".algorithmic-leaderboard__pair"),
    ];
    const measure = () => {
      const widest = Math.max(0, ...pairs.map((el) => el.offsetWidth));
      list.style.setProperty("--leaderboard-pair", `${widest}px`);
    };
    measure();
    // Re-measures once the webfont lands and the labels change width.
    const observer = new ResizeObserver(measure);
    pairs.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [rows]);

  return ref;
};

// Outlasts the row's 0.4s reveal, in case its transitionend never fires
const REVEAL_FALLBACK_MS = 600;
// Longer than a smooth scroll takes; scrollend isn't in every Safari yet
const AUTO_SCROLL_MS = 1000;
const REVEAL_MARGIN = 16;

// Once a newly opened row has finished growing, scrolls up just enough to show
// its bottom, without pushing its head under the stuck filter bar.
const revealIntoView = (row: HTMLElement, onScroll: () => void) => {
  const reveal = row.querySelector<HTMLElement>(".order-row__reveal");
  let done = false;

  const run = () => {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    reveal?.removeEventListener("transitionend", onEnd);
    if (!row.isConnected || !row.classList.contains("order-row--open")) return;

    const { top, bottom } = row.getBoundingClientRect();
    const overflow = bottom + REVEAL_MARGIN - window.innerHeight;
    if (overflow <= 0) return;
    const bar = document.querySelector(".algorithmic-leaderboard__bar");
    const ceiling = STICKY_TOP + (bar?.getBoundingClientRect().height ?? 0);
    const by = Math.min(overflow, top - ceiling);
    if (by <= 0) return;

    onScroll();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    window.scrollBy({ top: by, behavior: reduce.matches ? "auto" : "smooth" });
  };

  const onEnd = (e: TransitionEvent) => {
    if (e.target === reveal && e.propertyName === "grid-template-rows") run();
  };
  reveal?.addEventListener("transitionend", onEnd);
  const fallback = setTimeout(run, REVEAL_FALLBACK_MS);
};

// Scrolling, or closing a row, lifts the fade on the rows around an open one.
// Opening a row scrolls it into view, which must not count as scrolling.
const useScrollFocusOverride = (ref: RefObject<HTMLDivElement | null>) => {
  const [focusOverride, setFocusOverride] = useState(false);

  useEffect(() => {
    const list = ref.current;
    if (!list) return;

    let autoScrolling = false;
    let autoScrollTimer: ReturnType<typeof setTimeout> | undefined;
    const onAutoScroll = () => {
      autoScrolling = true;
      clearTimeout(autoScrollTimer);
      autoScrollTimer = setTimeout(() => {
        autoScrolling = false;
      }, AUTO_SCROLL_MS);
    };

    const onScroll = (e: Event) => {
      if (autoScrolling) return;
      const { target } = e;
      if (
        target === document ||
        (target instanceof Node && target.contains(list))
      )
        setFocusOverride(true);
    };
    document.addEventListener("scroll", onScroll, {
      capture: true,
      passive: true,
    });

    // Rows own their open state, so watch their open class change
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(({ target, oldValue }) => {
        if (!(target instanceof HTMLElement)) return;
        const wasOpen = !!oldValue?.includes("order-row--open");
        const isOpen = target.classList.contains("order-row--open");
        if (wasOpen === isOpen) return;
        setFocusOverride(wasOpen);
        if (isOpen) revealIntoView(target, onAutoScroll);
      });
    });
    observer.observe(list, {
      subtree: true,
      attributeFilter: ["class"],
      attributeOldValue: true,
    });

    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      observer.disconnect();
      clearTimeout(autoScrollTimer);
    };
  }, [ref]);

  return focusOverride;
};

export const LeaderboardRows: FC<{
  rows: Row[];
  status: FinRangeStatus;
  isLoadingNext: boolean;
  backfilling: boolean;
}> = ({ rows, status, isLoadingNext, backfilling }) => {
  const ref = usePairWidth(rows);
  const focusOverride = useScrollFocusOverride(ref);
  // With the pair and APR columns both aligned, every range indicator gets the same width
  const valuesRef = useAlignedValues(rows);

  return (
    <>
      <div
        ref={ref}
        className={clsx("algorithmic-leaderboard__list", {
          "algorithmic-leaderboard__reserve": backfilling,
        })}>
        {rows.length > 0 && (
          <div ref={valuesRef}>
            <OrderRows
              className={clsx("algorithmic-leaderboard__rows", {
                "algorithmic-leaderboard__rows--focus-override": focusOverride,
              })}>
              {rows.map((row) => (
                <LeaderboardRow key={row.node.id} row={row} status={status} />
              ))}
            </OrderRows>
          </div>
        )}
      </div>
      {isLoadingNext && (
        <p className="text-center color-grey p-3 mb-0">
          Searching more positions…
        </p>
      )}
      {rows.length === 0 && !backfilling && (
        <p className="text-center color-grey p-3 mb-0">
          No positions match the current filters.
        </p>
      )}
    </>
  );
};
